import { Stack } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPFontIcon, IPLabel } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { Info } from 'components/Notification/Info';
import { KeyValueTags } from 'components/Tags/KeyValueTag';
import { canUpdateTaskField, TaskFieldTypes } from 'components/Tasks/TaskAuthHelper';
import { TaskTagList } from 'components/Tasks/TaskTagList';
import { globalStackTokensGapExtraSmall, globalStackTokensGapMedium } from 'globalStyles';
import Tag from 'models/tag';
import Task from 'models/tasks/task';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsInTaskDetailCallout } from './TaskDetailRow';
import { iconFontSize, readOnlyRowSpace } from './TaskDetailStyles';

interface ITaskDetailTagsProps {
  orgTask: Task;
  task: Task;
  tags: Tag[];
  isActionPending: boolean;
  canUpdate: boolean;
  fixedTagIds?: number[];
  addTagToTaskState: (tag: Tag) => void;
  removeTagFromTaskState: (tag: Tag) => void;
}

export const TaskDetailTags = (props: ITaskDetailTagsProps) => {
  const isInCallout = useIsInTaskDetailCallout();
  const { t } = useTranslation(['tags', 'translation']);
  const appContext = useContext(AppContext);
  const isReadonly = !canUpdateTaskField(props.orgTask, props.canUpdate, TaskFieldTypes.Tags, appContext);
  const [fixedTags] = useState<Tag[]>(appContext.globalDataCache.tags.getItemsForId(props.fixedTagIds));

  //
  // Main render
  //
  return (
    <IPStack
      horizontal
      tokens={appContext.isMobileView ? globalStackTokensGapExtraSmall : globalStackTokensGapMedium}
      verticalAlign="center"
      styles={isReadonly ? { root: { paddingBottom: readOnlyRowSpace / 2 } } : undefined}
    >
      {!isInCallout && (
        <IPStack.Item>{!appContext.isMobileView && <IPFontIcon iconName="Tag" style={iconFontSize} />}</IPStack.Item>
      )}
      <IPStack.Item grow>
        <IPStack tokens={globalStackTokensGapExtraSmall}>
          {/* Show no data indication when when readonly and there are no fixed tags and no other tags */}
          {props.tags.length === 0 && props.fixedTagIds?.length === 0 && isReadonly && (
            <IPLabel>{t('tags:General.NoData')}</IPLabel>
          )}
          {/* Show fixed tags from the master task */}
          {props.fixedTagIds && props.fixedTagIds.length > 0 && (
            <IPStack horizontal tokens={globalStackTokensGapExtraSmall} verticalAlign="center">
              <Stack.Item>
                <KeyValueTags tags={fixedTags} compact={true} />
              </Stack.Item>
              <Stack.Item>
                <Info message={t('task:MonitoringMasterTask.TagInfo')} />
              </Stack.Item>
            </IPStack>
          )}
          {/* Show tags to edit or readonly */}
          {(props.tags.length !== 0 || !isReadonly) && (
            <TaskTagList
              tags={props.tags}
              isLoading={props.isActionPending}
              addTagToTaskState={props.addTagToTaskState}
              removeTagFromTaskState={props.removeTagFromTaskState}
              readOnly={isReadonly}
              disallowedTags={fixedTags}
            />
          )}
        </IPStack>
      </IPStack.Item>
    </IPStack>
  );
};
